import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Users, CheckCircle, Clock, Phone, MapPin, Edit, Trash2, Eye, Calendar } from 'lucide-react';
import { notaryService } from '../../services/notaryService';
import NotaryFormModal from './components/NotaryFormModal';
import NotaryDetailsModal from './components/NotaryDetailsModal';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import BulkActionsPanel from './components/BulkActionsPanel';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SelectUI from '../../components/ui/Select';
import EmptyState from '../../components/ui/EmptyState';

const NotaryManagementDashboard = () => {
  const [notaries, setNotaries] = useState([]);
  const [filteredNotaries, setFilteredNotaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedNotaries, setSelectedNotaries] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedNotary, setSelectedNotary] = useState(null);
  const [currentView, setCurrentView] = useState('list');
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    pending: 0,
    appointmentsThisMonth: 0
  });

  useEffect(() => {
    loadNotariesData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filterStatus, notaries]);

  const loadNotariesData = async () => {
    try {
      setLoading(true);
      const notariesResponse = await notaryService?.getAllNotaries();
      const appointmentStatsResponse = await notaryService?.getAppointmentStats();
      const notariesData = Array.isArray(notariesResponse) ? notariesResponse : (notariesResponse?.data || []);
      const appointmentStats = appointmentStatsResponse?.data || appointmentStatsResponse || { thisMonth: 0, total: 0 };
      const validNotariesData = Array.isArray(notariesData) ? notariesData : [];
      setNotaries(validNotariesData);
      const totalNotaries = validNotariesData?.length || 0;
      const verifiedNotaries = validNotariesData?.filter(n => n?.is_verified)?.length || 0;
      const pendingNotaries = totalNotaries - verifiedNotaries;
      setStats({
        total: totalNotaries,
        verified: verifiedNotaries,
        pending: pendingNotaries,
        appointmentsThisMonth: appointmentStats?.thisMonth || 0
      });
    } catch (error) {
      console.error('Error loading notaries:', error);
      setNotaries([]);
      setStats({ total: 0, verified: 0, pending: 0, appointmentsThisMonth: 0 });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const safeNotaries = Array.isArray(notaries) ? notaries : [];
    let filtered = [...safeNotaries];
    if (searchTerm?.trim()) {
      const term = searchTerm?.toLowerCase();
      filtered = filtered?.filter(notary =>
        notary?.user_profile?.full_name?.toLowerCase()?.includes(term) ||
        notary?.license_number?.toLowerCase()?.includes(term) ||
        notary?.office_address?.toLowerCase()?.includes(term) ||
        (Array.isArray(notary?.specializations) && notary?.specializations?.some(spec => spec?.toLowerCase()?.includes(term)))
      );
    }
    if (filterStatus !== 'all') {
      if (filterStatus === 'verified') {
        filtered = filtered?.filter(notary => notary?.is_verified === true);
      } else if (filterStatus === 'pending') {
        filtered = filtered?.filter(notary => notary?.is_verified === false);
      }
    }
    setFilteredNotaries(Array.isArray(filtered) ? filtered : []);
  };

  const handleSelectNotary = (notaryId, checked) => {
    if (checked) {
      setSelectedNotaries(prev => [...prev, notaryId]);
    } else {
      setSelectedNotaries(prev => prev?.filter(id => id !== notaryId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const safeFilteredNotaries = Array.isArray(filteredNotaries) ? filteredNotaries : [];
      setSelectedNotaries(safeFilteredNotaries?.map(n => n?.id) || []);
    } else {
      setSelectedNotaries([]);
    }
  };

  const handleViewDetails = (notary) => {
    setSelectedNotary(notary);
    setShowDetailsModal(true);
  };

  const handleEditNotary = (notary) => {
    setSelectedNotary(notary);
    setShowAddModal(true);
  };

  const handleDeleteNotary = async (notaryId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce notaire ?')) {
      try {
        await loadNotariesData();
      } catch (error) {
        console.error('Error deleting notary:', error);
      }
    }
  };

  const getStatusIcon = (isVerified) => {
    return isVerified ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <Clock className="w-4 h-4 text-yellow-500" />
    );
  };

  const getStatusText = (isVerified) => isVerified ? 'Vérifié' : 'En attente';

  const getStatusBadgeClass = (isVerified) => {
    return isVerified
      ? 'bg-green-100 text-green-800 border-green-200' :'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'verified', label: 'Vérifiés' },
    { value: 'pending', label: 'En attente' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Gestion des Notaires
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Gérez les profils et partenariats notariaux dans l'écosystème KAPITALSTATES
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setCurrentView(currentView === 'list' ? 'analytics' : 'list')}
              >
                {currentView === 'list' ? 'Analytics' : 'Liste'}
              </Button>
              <Button
                variant="elegant"
                onClick={() => {
                  setSelectedNotary(null);
                  setShowAddModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un Notaire
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {currentView === 'analytics' ? (
          <AnalyticsDashboard stats={stats} notaries={notaries} />
        ) : (
          <>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-8 w-8 text-blue-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Total Notaires</p>
                    <p className="text-2xl font-semibold text-foreground">{stats?.total}</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Vérifiés</p>
                    <p className="text-2xl font-semibold text-foreground">{stats?.verified}</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-8 w-8 text-yellow-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">En Attente</p>
                    <p className="text-2xl font-semibold text-foreground">{stats?.pending}</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Calendar className="h-8 w-8 text-purple-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">RDV ce mois</p>
                    <p className="text-2xl font-semibold text-foreground">{stats?.appointmentsThisMonth}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-card border border-border rounded-lg mb-6">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        type="text"
                        placeholder="Rechercher par nom, licence, adresse ou spécialisation..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e?.target?.value)}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <SelectUI
                      value={filterStatus}
                      onChange={(val) => setFilterStatus(val)}
                      options={statusOptions}
                      placeholder="Tous les statuts"
                    />
                    <Button variant="outline">
                      <Filter className="w-4 h-4 mr-2" />
                      Filtres
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedNotaries?.length > 0 && (
              <BulkActionsPanel
                selectedCount={selectedNotaries?.length}
                onClearSelection={() => setSelectedNotaries([])}
                onBulkVerify={async () => {
                  await loadNotariesData();
                  setSelectedNotaries([]);
                }}
                onBulkDelete={async () => {
                  if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedNotaries?.length} notaire(s) ?`)) {
                    await loadNotariesData();
                    setSelectedNotaries([]);
                  }
                }}
              />
            )}

            {/* Notaries Table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedNotaries?.length === filteredNotaries?.length && filteredNotaries?.length > 0}
                          onChange={(e) => handleSelectAll(e?.target?.checked)}
                          className="h-4 w-4 rounded border-border text-brand-gold focus:ring-brand-gold/30"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Notaire
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Licence
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Spécialisations
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {filteredNotaries?.map((notary) => (
                      <tr key={notary?.id} className="hover:bg-accent transition-colors">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedNotaries?.includes(notary?.id)}
                            onChange={(e) => handleSelectNotary(notary?.id, e?.target?.checked)}
                            className="h-4 w-4 rounded border-border text-brand-gold focus:ring-brand-gold/30"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-muted/40 border border-border flex items-center justify-center">
                                <Users className="w-5 h-5 text-muted-foreground" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-foreground">
                                {notary?.user_profile?.full_name || notary?.notary_name || 'N/A'}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {notary?.user_profile?.email || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-foreground">
                            {notary?.license_number || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(notary?.specializations) && notary?.specializations?.slice(0, 2)?.map((spec, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted/40 border border-border text-foreground"
                              >
                                {spec}
                              </span>
                            ))}
                            {Array.isArray(notary?.specializations) && notary?.specializations?.length > 2 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted/40 border border-border text-muted-foreground">
                                +{notary?.specializations?.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeClass(notary?.is_verified)}`}>
                            {getStatusIcon(notary?.is_verified)}
                            <span className="ml-1">{getStatusText(notary?.is_verified)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col space-y-1">
                            {notary?.user_profile?.phone && (
                              <div className="flex items-center text-sm text-muted-foreground">
                                <Phone className="w-4 h-4 mr-1" />
                                {notary?.user_profile?.phone}
                              </div>
                            )}
                            {notary?.office_address && (
                              <div className="flex items-center text-sm text-muted-foreground">
                                <MapPin className="w-4 h-4 mr-1" />
                                {notary?.office_address?.slice(0, 30)}...
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end space-x-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewDetails(notary)}
                              title="Voir les détails"
                            >
                              <Eye className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditNotary(notary)}
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4 text-green-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteNotary(notary?.id)}
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredNotaries?.length === 0 && !loading && (
                <EmptyState
                  iconName="UserX"
                  title="Aucun notaire trouvé"
                  description={
                    searchTerm || filterStatus !== 'all' ?'Aucun notaire ne correspond à vos critères de recherche.' :'Commencez par ajouter un nouveau notaire.'
                  }
                  ctaLabel={(!searchTerm && filterStatus === 'all') ? 'Ajouter un Notaire' : undefined}
                  ctaIcon="Plus"
                  onCta={(!searchTerm && filterStatus === 'all') ? () => { setSelectedNotary(null); setShowAddModal(true); } : undefined}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <NotaryFormModal
          notary={selectedNotary}
          onClose={() => {
            setShowAddModal(false);
            setSelectedNotary(null);
          }}
          onSuccess={() => {
            loadNotariesData();
            setShowAddModal(false);
            setSelectedNotary(null);
          }}
        />
      )}
      {showDetailsModal && selectedNotary && (
        <NotaryDetailsModal
          notary={selectedNotary}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedNotary(null);
          }}
          onEdit={() => {
            setShowDetailsModal(false);
            setShowAddModal(true);
          }}
        />
      )}
    </div>
  );
};

export default NotaryManagementDashboard;